import React, { Suspense, useCallback, useMemo, useState } from "react";
import {
  graphql,
  loadQuery,
  useLazyLoadQuery,
  useRelayEnvironment,
  useSubscription,
} from "react-relay";
import { RoutineOccasionScreenQuery } from "./__generated__/RoutineOccasionScreenQuery.graphql";
import { RoutineOccasionScreenTaskChangedSubscription } from "./__generated__/RoutineOccasionScreenTaskChangedSubscription.graphql";

import { useFocusEffect } from "@react-navigation/native";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { RoutineOccasionScreenReloadStatusQuery } from "./__generated__/RoutineOccasionScreenReloadStatusQuery.graphql";

import { useNewFetchKeyOnNav } from "../../../useNewFetchKeyOnNav";
import { RoutineOccasionList } from "./RoutineOccasionList";
import { RoutineOccasionScreenRoutineDescription } from "./RoutineOccasionScreenRoutineDescription";
import {
  RoutineOccasionNavigationProp,
  RoutineOccasionScreenProps,
} from "./RoutineOccasionStack";
import { RoutineOccasionHistory } from "../RoutineOccasionHistoryScreen";
import { useTodoBottomSheet } from "../../ReportIssueBottomSheet/TodoBottomSheet/TodoBottomSheetProvider";
import { useSubscriptionWithSkip } from "widgets/src/utils/useSubscriptionWithSkip";
import { RoutineOccasionScreenTodoChangedSubscription } from "./__generated__/RoutineOccasionScreenTodoChangedSubscription.graphql";
import { StatusBar } from "expo-status-bar";
import { useHasPermissions } from "@/src/OS/Login/usePermissions";

export type NavProp = RoutineOccasionNavigationProp<"RoutineOccasionMain">;

type RoutineOccasionScreenType = {
  navigation: NavProp;
  routineId: string;
  sequenceNumber: number;
  storeId?: string;
  taskId?: string;

  onRequestBottomSheet: () => void;
};

const RoutineOccasionC = (
  props: RoutineOccasionScreenType & { fetchKey: string }
) => {
  const { routineId, sequenceNumber, storeId } = props;
  const todoBottomSheet = useTodoBottomSheet();
  const relay = useRelayEnvironment();

  const reloadStatus = useCallback(() => {
    loadQuery<RoutineOccasionScreenReloadStatusQuery>(
      relay,
      graphql`
        query RoutineOccasionScreenReloadStatusQuery(
          $routineId: ID!
          $sequenceNumber: Int!
          $storeId: ID
        ) {
          routine(id: $routineId) {
            occasion(sequenceNumber: $sequenceNumber, storeId: $storeId) {
              id
              status
            }
          }
        }
      `,
      {
        routineId,
        storeId,
        sequenceNumber,
      },
      { fetchPolicy: "store-and-network" }
    );
  }, [relay, routineId, sequenceNumber, storeId]);

  useFocusEffect(useCallback(() => () => reloadStatus(), [reloadStatus]));

  useRoutineOccasionSubscriptions(routineId);

  const navFetchKey = useNewFetchKeyOnNav();

  const data = useLazyLoadQuery<RoutineOccasionScreenQuery>(
    graphql`
      query RoutineOccasionScreenQuery(
        $routineId: ID!
        $sequenceNumber: Int!
        $storeId: ID
      ) {
        currentStore {
          ...usePermissions_store
        }
        routine(id: $routineId) {
          id
          isArchived
          ...RoutineOccasionScreenRoutineDescription_Routine
          ...RoutineOccasionList_Routine
        }
      }
    `,
    {
      routineId,
      sequenceNumber,
      storeId,
    },
    {
      fetchKey: navFetchKey + props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const requestBottomSheet = useCallback(() => {
    reloadStatus();
    props.onRequestBottomSheet();
  }, [props, reloadStatus]);

  const hasRoutineAdmin = useHasPermissions(data.currentStore, "ROUTINE_ADMIN");
  if (!data.routine) {
    return null;
  }
  return (
    <Suspense
      fallback={
        <RoutineOccasionScreenRoutineDescription
          routine={data.routine}
          hasRoutineAdmin={hasRoutineAdmin}
          onCustomise={() => {
            if (data.routine?.id) {
              props.navigation.navigate("RoutineStoreCustomize", {
                routineId: data.routine?.id,
                storeId: storeId,
              });
            }
          }}
        />
      }
    >
      <RoutineOccasionList
        navigation={props.navigation}
        routineKey={data.routine}
        onRequestBottomSheet={requestBottomSheet}
        sequenceNumber={sequenceNumber}
        isArchived={data.routine.isArchived ?? false}
        presentTodo={todoBottomSheet.present}
        createIssue={todoBottomSheet.createIssue}
        taskId={props.taskId}
        storeId={storeId}
      />
    </Suspense>
  );
};

const RountineOccasion = React.memo(RoutineOccasionC);

export const RoutineOccasionScreen = (
  props: RoutineOccasionScreenProps<"RoutineOccasionMain">
) => {
  const [historyOpen, setHistoryOpen] = useState(false);

  const onRequestOccasionsHistory = useCallback(() => {
    setHistoryOpen(true);
  }, []);

  const onSelectOccasion = useCallback(
    (sequenceNumber: number) => {
      setHistoryOpen(false);
      props.navigation.setParams({
        sequenceNumber,
      });
    },
    [props.navigation]
  );

  const render = useCallback(
    (fetchKey: string) => (
      <RountineOccasion
        navigation={props.navigation}
        onRequestBottomSheet={onRequestOccasionsHistory}
        routineId={props.route.params.routineId}
        storeId={props.route.params.storeId}
        sequenceNumber={props.route.params.sequenceNumber}
        fetchKey={fetchKey}
        taskId={props.route.params.taskId}
      />
    ),
    [
      onRequestOccasionsHistory,
      props.navigation,
      props.route.params.routineId,
      props.route.params.sequenceNumber,
      props.route.params.storeId,
      props.route.params.taskId,
    ]
  );

  return (
    <>
      <StatusBar style="dark" />
      <LazyQueryNetworkBoundrary>{render}</LazyQueryNetworkBoundrary>
      <RoutineOccasionHistory
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onSelectOccasion={onSelectOccasion}
        routineId={props.route.params.routineId}
        sequenceNumber={props.route.params.sequenceNumber}
        storeId={props.route.params.storeId}
      />
    </>
  );
};

function useRoutineOccasionSubscriptions(routineId: string) {
  useSubscriptionWithSkip<RoutineOccasionScreenTodoChangedSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription RoutineOccasionScreenTodoChangedSubscription(
            $routineId: ID!
          ) {
            todoChangedByRoutine(routineId: $routineId) {
              ...RoutineOccasionTodoIssue_todo
            }
          }
        `,
        variables: { routineId },
      }),
      [routineId]
    )
  );

  useSubscription<RoutineOccasionScreenTaskChangedSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription RoutineOccasionScreenTaskChangedSubscription(
            $routineId: ID!
          ) {
            routineTaskExecutionChanged(routineIds: [$routineId]) {
              ...RoutineOccasionScreenListItemComponent_TaskExecution
              todos {
                id
                ...RoutineOccasionTodoIssue_todo
              }
            }
          }
        `,
        variables: { routineId },
      }),
      [routineId]
    )
  );
}
